import {pick} from '@benchling/js-utils';
import dagre from 'dagre';
import {Edge, Node} from 'reactflow';

import {assertAndContinue} from 'Asserts';

import calculateDimensionsForLayout, {
  DEFAULT_LAYOUT_SETTINGS,
  DimensionsForLayout,
  LayoutSettings,
} from './calculateDimensionsForLayout';
import {unhideFlowchartElementAfterLayout} from './hideFlowchartElementBeforeLayoutUtils';

interface FlowchartData<N extends Node, E extends Edge> {
  nodes: Array<N>;
  edges: Array<E>;
}

/**
 * Arrange nodes for a `reactflow` flowchart, based on pre-calculated dimensions of the nodes and edge labels.
 *
 * @param flowchartData The nodes and edges for the `reactflow` flow chart.
 * @param dimensions Calculated dimensions of each nodes and edge, and spacing between nodes
 * @returns A copy of `flowchartData`, with `position` updated on the nodes
 *
 * @package
 */
export function layoutFlowchartFromDimensions<N extends Node, E extends Edge>(
  {nodes, edges}: FlowchartData<N, E>,
  dimensions: DimensionsForLayout
): FlowchartData<N, E> {
  const dagreGraph = new dagre.graphlib.Graph();

  dagreGraph.setGraph({
    rankdir: 'LR', // 'LR' = arrange left to right
    ranksep: dimensions.horizontalSpaceBetweenNodes,
    nodesep: dimensions.verticalSpaceBetweenNodes,
  });
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  nodes.forEach((node) => {
    assertAndContinue(dimensions.nodes[node.id] != null, 'Node is missing dimensions needed for layout');
    const {height, width} = dimensions.nodes[node.id] ?? {height: 0, width: 0};
    dagreGraph.setNode(node.id, {height, width});
  });
  edges.forEach((edge) => {
    assertAndContinue(dimensions.edges[edge.id] != null, 'Edge is missing dimensions needed for layout');
    const {height, width} = dimensions.edges[edge.id] ?? {height: 0, width: 0};
    dagreGraph.setEdge(edge.source, edge.target, {height, width});
  });

  dagre.layout(dagreGraph);

  // Apply calculated positions, and un-hide any elements that were temporarily hidden.
  return {
    nodes: nodes.map((node) =>
      unhideFlowchartElementAfterLayout({...node, position: pick(dagreGraph.node(node.id), 'x', 'y')})
    ),
    edges: edges.map((edge) => unhideFlowchartElementAfterLayout(edge)),
  };
}

/**
 * Arrange nodes for a `reactflow` flowchart, by calculating the dimensions' of the nodes and edge labels.
 *
 * To prevent new nodes or edges from showing until their positions have been calculated, initialize the new
 * nodes and edges with `hideFlowchartElementBeforeLayout`. `layoutFlowchart` will automatically un-hide all
 * nodes and edges, after calculating their positions.
 *
 * This layout algorithm does not guarantee nodes do not overlap edge labels.
 *
 * @param flowchartData The nodes and edges for the `reactflow` flow chart.
 * @param customLayoutSettings Custom layout settings. See `DEFAULT_LAYOUT_SETTINGS` for defaults. By default,
 *    nodes' actual heights and widths, which are automatically calculated by `reactflow`, are used.
 * @returns A copy of `flowchartData`, with `position` updated on the nodes
 */
export default function layoutFlowchart<N extends Node, E extends Edge>(
  {nodes, edges}: FlowchartData<N, E>,
  customLayoutSettings: Partial<LayoutSettings<N, E>> = {}
): FlowchartData<N, E> {
  return layoutFlowchartFromDimensions(
    {nodes, edges},
    calculateDimensionsForLayout(nodes, edges, {...DEFAULT_LAYOUT_SETTINGS, ...customLayoutSettings})
  );
}
